import { useEffect, useRef, type MouseEvent, type ReactNode } from "react";
import { Link } from "react-router-dom";

/**
 * Same hint the Tickets "New ticket" button uses while a staff member is
 * previewing the portal as a client. View-as is read-only, so neither control
 * may open the form.
 */
export const READ_ONLY_VIEW_AS_TITLE = "Read-only while viewing as another user";

export const DASHBOARD_RAISE_KEY = "dashboard-raise";
export const CATALOG_RAISE_KEY = "catalog-raise";

type RaiseTicketReturn = { path: string; key: string };

// The quick action lives on another route. Clicking it navigates to
// /tickets?new=1, which unmounts the link, so the modal's usual
// "restore document.activeElement" lands on the page body. Remember the
// control here and, if the form is dismissed, go back and focus it.
let pendingReturn: RaiseTicketReturn | null = null;
let pendingFocusKey: string | null = null;

export function armRaiseTicketReturn(target: RaiseTicketReturn) {
  pendingReturn = target;
}

export function takeRaiseTicketReturn(): RaiseTicketReturn | null {
  const value = pendingReturn;
  pendingReturn = null;
  return value;
}

export function requestRaiseTicketFocus(key: string) {
  pendingFocusKey = key;
}

export function peekRaiseTicketFocus(key: string): boolean {
  return pendingFocusKey === key;
}

/** True once, when this control should receive focus after a dismissed form. */
export function takeRaiseTicketFocus(key: string): boolean {
  if (pendingFocusKey !== key) return false;
  pendingFocusKey = null;
  return true;
}

export function clearRaiseTicketReturn() {
  pendingReturn = null;
  pendingFocusKey = null;
}

/**
 * "Raise a ticket" entry that deep-links to the New ticket form.
 * In View-as it is a disabled button with the same tooltip as New ticket,
 * and it does not navigate. Otherwise it is the existing link; dismissing
 * the form returns focus to this control.
 */
export function RaiseTicketLink({
  className,
  impersonating,
  returnPath,
  focusKey,
  children,
}: {
  className: string;
  impersonating: boolean;
  returnPath: string;
  focusKey: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLAnchorElement>(null);

  // Defer past the New ticket modal's own focus restore, which runs in the
  // same commit and would otherwise leave focus on the page body.
  useEffect(() => {
    const el = ref.current;
    if (!el || !peekRaiseTicketFocus(focusKey)) return;
    const id = window.setTimeout(() => {
      if (takeRaiseTicketFocus(focusKey)) el.focus();
    }, 0);
    return () => window.clearTimeout(id);
  });

  if (impersonating) {
    return (
      <button type="button" className={className} disabled title={READ_ONLY_VIEW_AS_TITLE}>
        {children}
      </button>
    );
  }

  function arm(event: MouseEvent<HTMLAnchorElement>) {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }
    armRaiseTicketReturn({ path: returnPath, key: focusKey });
  }

  return (
    <Link ref={ref} className={className} to="/tickets?new=1" onClick={arm}>
      {children}
    </Link>
  );
}
